import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import { Box, Button, Card, Container, Grid, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Footer, MarketingFinalCta } from '../components/homepage';
import { DEMO_REQUEST_MAILTO, INTEREST_LIST_MAILTO } from '../config/contact';
import { getCaseStudyBySlug } from '../content/caseStudies';
import {
  marketingCardSx,
  marketingDisplayFont,
  marketingHeroGridSx,
  marketingMutedSectionSx,
  marketingPalette,
  marketingPrimaryButtonSx,
  marketingSecondaryButtonSx,
  marketingSectionSx,
} from '../components/homepage/marketingSystem';

const CaseStudyPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const caseStudy = slug ? getCaseStudyBySlug(slug) : undefined;

  if (!caseStudy) {
    return <Navigate to="/case-studies" replace />;
  }

  const [primaryHighlight, ...secondaryHighlights] = caseStudy.highlights;

  // Parse primary highlight value — support "57% -> 72%", "−8%", ">40%", etc.
  const parsedPrimary = (() => {
    const raw = primaryHighlight.value.trim();
    const arrowMatch = raw.match(/^([^->\s]+)\s*(?:->|→)\s*([^->\s]+)$/);
    if (arrowMatch) {
      const [, before, after] = arrowMatch;
      const toNum = (s: string) => {
        const n = parseFloat(s.replace(/[^0-9.\-]/g, ''));
        return Number.isFinite(n) ? n : null;
      };
      const bNum = toNum(before);
      const aNum = toNum(after);
      if (bNum !== null && aNum !== null) {
        const scale = Math.max(bNum, aNum, 100);
        const delta = aNum - bNum;
        const deltaDisplay = `${delta >= 0 ? '+' : ''}${Math.round(delta)}${before.includes('%') ? ' pts' : ''}`;
        return {
          kind: 'beforeAfter' as const,
          beforeDisplay: before,
          afterDisplay: after,
          beforePct: Math.min(100, (bNum / scale) * 100),
          afterPct: Math.min(100, (aNum / scale) * 100),
          deltaDisplay,
        };
      }
    }
    return { kind: 'single' as const, display: raw };
  })();

  return (
    <Box className="marketing-layout" sx={{ width: '100%', overflowX: 'hidden' }}>
      <Helmet>
        <link rel="canonical" href={`https://ormal.io/case-studies/${slug}`} />
      </Helmet>
      {/* ============================== HERO ============================== */}
      <Box
        component="section"
        sx={{ ...marketingSectionSx, pt: { xs: 6, md: 9 }, pb: { xs: 5, md: 6 }, overflow: 'hidden' }}
      >
        <Box sx={marketingHeroGridSx} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            component={RouterLink}
            to="/case-studies"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              mb: 2.2,
              fontSize: '0.82rem',
              fontWeight: 400,
              letterSpacing: '0.02em',
              color: marketingPalette.muted,
              textDecoration: 'none',
              transition: 'color 150ms ease',
              '&:hover': { color: marketingPalette.accent },
            }}
          >
            <ArrowBack sx={{ fontSize: '0.95rem' }} />
            All case studies
          </Box>

          <Box sx={{ maxWidth: 860 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  fontWeight: 400,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: marketingPalette.accent,
                }}
              >
                Case Study
              </Typography>
              <Box sx={{ width: 24, height: 1, backgroundColor: 'rgba(15,111,255,0.4)' }} />
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 400,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: marketingPalette.muted,
                }}
              >
                {caseStudy.label}
              </Typography>
            </Box>

            <Typography
              component="h1"
              sx={{
                fontFamily: marketingDisplayFont,
                fontSize: { xs: '2.15rem', md: '3.1rem' },
                fontWeight: 400,
                letterSpacing: '-0.045em',
                lineHeight: 1.08,
                color: marketingPalette.headline,
                textWrap: 'balance',
                mb: 1.9,
              }}
            >
              {caseStudy.pageTitle}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1.05rem', md: '1.18rem' },
                lineHeight: 1.55,
                color: marketingPalette.body,
                textWrap: 'pretty',
                maxWidth: 720,
                mb: 3,
              }}
            >
              {caseStudy.summary}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.4 }}>
              <Button href={DEMO_REQUEST_MAILTO} sx={marketingPrimaryButtonSx}>
                Request Demo
              </Button>
              <Button component={RouterLink} to="/case-studies" sx={marketingSecondaryButtonSx}>
                All case studies
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ======================= HEADLINE RESULT ========================== */}
      <Box sx={{ pb: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <Card
            sx={{
              ...marketingCardSx,
              position: 'relative',
              overflow: 'hidden',
              p: { xs: 2.8, md: 3.4 },
              border: '1px solid rgba(196,212,232,0.96)',
              background: 'linear-gradient(180deg, #ffffff 0%, #f4f9ff 100%)',
              boxShadow: '0 24px 54px rgba(15,23,42,0.06)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: 'linear-gradient(90deg, #0f6fff, #0ea5e9)',
              },
              '&:hover': { transform: 'none' },
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: -60,
                right: -60,
                width: 240,
                height: 240,
                borderRadius: '50%',
                background: 'radial-gradient(closest-side, rgba(15,111,255,0.12), transparent 70%)',
                pointerEvents: 'none',
              }}
            />
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.6,
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                      fontWeight: 400,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: marketingPalette.accent,
                    }}
                  >
                    Headline result
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      maxWidth: 72,
                      height: 1,
                      background: 'linear-gradient(90deg, rgba(15,111,255,0.4), rgba(15,111,255,0))',
                    }}
                  />
                </Box>
                {parsedPrimary.kind === 'beforeAfter' && (
                  <Box
                    sx={{
                      px: 1.1,
                      py: 0.45,
                      borderRadius: 0.7,
                      backgroundColor: 'rgba(15,111,255,0.08)',
                      border: '1px solid rgba(15,111,255,0.28)',
                    }}
                  >
                    <Typography
                      sx={{
                        color: marketingPalette.accent,
                        fontFamily: 'monospace',
                        fontSize: '0.74rem',
                        fontWeight: 400,
                        letterSpacing: '0.04em',
                      }}
                    >
                      {parsedPrimary.deltaDisplay}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Grid container spacing={{ xs: 2.5, md: 4 }} alignItems="center">
                <Grid item xs={12} md={5}>
                  {parsedPrimary.kind === 'beforeAfter' ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.2, flexWrap: 'wrap', mb: 0.8 }}>
                        <Typography
                          sx={{
                            fontFamily: marketingDisplayFont,
                            color: marketingPalette.accent,
                            fontWeight: 400,
                            fontSize: { xs: '3rem', md: '3.6rem' },
                            lineHeight: 1,
                            letterSpacing: '-0.045em',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {parsedPrimary.afterDisplay}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          color: marketingPalette.headline,
                          fontSize: { xs: '1rem', md: '1.08rem' },
                          fontWeight: 400,
                          letterSpacing: '-0.005em',
                        }}
                      >
                        {primaryHighlight.label}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography
                        sx={{
                          fontFamily: marketingDisplayFont,
                          color: marketingPalette.accent,
                          fontWeight: 400,
                          fontSize: { xs: '3rem', md: '3.6rem' },
                          lineHeight: 1,
                          letterSpacing: '-0.045em',
                          fontVariantNumeric: 'tabular-nums',
                          mb: 0.8,
                        }}
                      >
                        {parsedPrimary.display}
                      </Typography>
                      <Typography
                        sx={{ color: marketingPalette.headline, fontSize: '1.08rem', fontWeight: 400 }}
                      >
                        {primaryHighlight.label}
                      </Typography>
                    </>
                  )}
                </Grid>

                <Grid item xs={12} md={7}>
                  {parsedPrimary.kind === 'beforeAfter' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, mb: 1.4 }}>
                      {[
                        {
                          label: 'Before',
                          display: parsedPrimary.beforeDisplay,
                          pct: parsedPrimary.beforePct,
                          isAfter: false,
                        },
                        {
                          label: 'After',
                          display: parsedPrimary.afterDisplay,
                          pct: parsedPrimary.afterPct,
                          isAfter: true,
                        },
                      ].map((row) => (
                        <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                          <Typography
                            sx={{
                              width: 64,
                              color: marketingPalette.muted,
                              fontSize: '0.64rem',
                              fontWeight: 400,
                              letterSpacing: '0.12em',
                              textTransform: 'uppercase',
                              fontFamily: 'monospace',
                            }}
                          >
                            {row.label}
                          </Typography>
                          <Box
                            sx={{
                              flex: 1,
                              height: 10,
                              borderRadius: 5,
                              backgroundColor: 'rgba(15,23,42,0.06)',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: `${row.pct}%`,
                                background: row.isAfter
                                  ? 'linear-gradient(90deg, #0f6fff 0%, #0ea5e9 100%)'
                                  : 'rgba(100,116,139,0.55)',
                                borderRadius: 5,
                                transition: 'width 400ms ease',
                              }}
                            />
                          </Box>
                          <Typography
                            sx={{
                              width: 64,
                              textAlign: 'right',
                              color: row.isAfter ? marketingPalette.accent : marketingPalette.body,
                              fontWeight: 400,
                              fontSize: '0.92rem',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {row.display}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : null}
                  <Typography
                    sx={{
                      color: marketingPalette.body,
                      lineHeight: 1.72,
                      fontSize: { xs: '0.95rem', md: '1rem' },
                    }}
                  >
                    {primaryHighlight.detail}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Container>
      </Box>

      {/* ==================== SECONDARY HIGHLIGHTS STRIP ==================== */}
      {secondaryHighlights.length ? (
        <Box sx={{ pb: { xs: 4, md: 5 } }}>
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: `repeat(${Math.min(secondaryHighlights.length, 3)}, 1fr)`,
                },
                gap: { xs: 1.5, md: 2 },
              }}
            >
              {secondaryHighlights.map((item) => (
                <Box
                  key={`${item.label}-${item.value}`}
                  sx={{
                    position: 'relative',
                    px: { xs: 2.5, md: 2.8 },
                    py: { xs: 2.4, md: 2.6 },
                    borderRadius: 1.25,
                    border: '1px solid rgba(196,212,232,0.96)',
                    background: 'linear-gradient(180deg, #ffffff 0%, #fafbfd 100%)',
                    boxShadow: '0 14px 34px rgba(15,23,42,0.045)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.66rem',
                      fontWeight: 400,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: marketingPalette.muted,
                      mb: 1.1,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: marketingDisplayFont,
                      fontSize: { xs: '1.65rem', md: '1.95rem' },
                      fontWeight: 400,
                      letterSpacing: '-0.035em',
                      lineHeight: 1.05,
                      color: marketingPalette.headline,
                      fontVariantNumeric: 'tabular-nums',
                      mb: 1,
                    }}
                  >
                    {item.value}
                  </Typography>
                  <Typography sx={{ color: marketingPalette.body, fontSize: '0.88rem', lineHeight: 1.55 }}>
                    {item.detail}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      ) : null}

      {/* ======================== THE SITUATION ========================= */}
      <Box component="section" sx={{ ...marketingMutedSectionSx, py: { xs: 5.5, md: 7 } }}>
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'inline-block',
              mb: 1.75,
              px: 1.4,
              py: 0.45,
              borderRadius: '999px',
              backgroundColor: marketingPalette.accentSoft,
              border: `1px solid ${marketingPalette.accentBorder}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 400,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: marketingPalette.accent,
              }}
            >
              The situation
            </Typography>
          </Box>

          <Typography
            component="h2"
            sx={{
              fontFamily: marketingDisplayFont,
              fontSize: { xs: '1.75rem', md: '2.35rem' },
              fontWeight: 400,
              letterSpacing: '-0.04em',
              color: marketingPalette.headline,
              lineHeight: 1.14,
              textWrap: 'balance',
              mb: 2,
            }}
          >
            {caseStudy.problemTitle}
          </Typography>

          <Typography
            sx={{
              color: marketingPalette.headline,
              fontWeight: 400,
              lineHeight: 1.55,
              fontSize: { xs: '1.12rem', md: '1.22rem' },
              letterSpacing: '-0.005em',
              mb: 2.2,
            }}
          >
            {caseStudy.problemBody}
          </Typography>

          <Typography
            sx={{
              color: marketingPalette.body,
              lineHeight: 1.82,
              fontSize: '1rem',
            }}
          >
            {caseStudy.impactStatement}
          </Typography>

          {/* Meta ribbon */}
          <Box
            sx={{
              mt: 4,
              pt: 2.5,
              borderTop: '1px solid rgba(196,212,232,0.7)',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 1.5, md: 2.5 },
            }}
          >
            {caseStudy.meta.map((item) => (
              <Box key={item.label}>
                <Typography
                  sx={{
                    fontSize: '0.66rem',
                    fontWeight: 400,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: marketingPalette.muted,
                    mb: 0.6,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    color: marketingPalette.headline,
                    fontWeight: 400,
                    lineHeight: 1.58,
                    fontSize: '0.94rem',
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ================== SCENARIO PATTERNS (numbered bullets) ================= */}
      <Box component="section" sx={marketingSectionSx}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 740, mb: { xs: 3.5, md: 4.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.4 }}>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  fontWeight: 400,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: marketingPalette.accent,
                }}
              >
                What we saw in the file
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: 72,
                  height: 1,
                  background: 'linear-gradient(90deg, rgba(15,111,255,0.4), rgba(15,111,255,0))',
                }}
              />
            </Box>
            <Typography
              component="h2"
              sx={{
                fontFamily: marketingDisplayFont,
                fontSize: { xs: '1.75rem', md: '2.35rem' },
                fontWeight: 400,
                letterSpacing: '-0.04em',
                color: marketingPalette.headline,
                lineHeight: 1.12,
                textWrap: 'balance',
                mb: 1.3,
              }}
            >
              Different payment contexts, not one generic checkout
            </Typography>
            <Typography
              sx={{ color: marketingPalette.body, lineHeight: 1.72, fontSize: { xs: '1rem', md: '1.06rem' } }}
            >
              The payment file did not look like one uniform stream. Distinct patterns needed distinct
              responses — Ormal built the payment logic around them.
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 820, display: 'grid', gap: 1.5 }}>
            {caseStudy.scenarioPatterns.map((item, bulletIndex) => (
              <Box
                key={item}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '36px minmax(0, 1fr)',
                  gap: 1.6,
                  alignItems: 'start',
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: marketingPalette.accentSoft,
                    border: `1px solid ${marketingPalette.accentBorder}`,
                    color: marketingPalette.accent,
                    fontSize: '0.78rem',
                    fontWeight: 400,
                    fontFamily: 'monospace',
                    mt: 0.15,
                  }}
                >
                  {bulletIndex + 1}
                </Box>
                <Typography
                  sx={{
                    color: marketingPalette.body,
                    lineHeight: 1.78,
                    fontSize: '0.98rem',
                    pt: 0.15,
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* =================== HOW ORMAL HELPED (themed cards) ==================== */}
      <Box component="section" sx={marketingMutedSectionSx}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 740, mb: { xs: 3.5, md: 4.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.4 }}>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  fontWeight: 400,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: marketingPalette.accent,
                }}
              >
                How Ormal helped
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: 72,
                  height: 1,
                  background: 'linear-gradient(90deg, rgba(15,111,255,0.4), rgba(15,111,255,0))',
                }}
              />
            </Box>
            <Typography
              component="h2"
              sx={{
                fontFamily: marketingDisplayFont,
                fontSize: { xs: '1.75rem', md: '2.35rem' },
                fontWeight: 400,
                letterSpacing: '-0.04em',
                color: marketingPalette.headline,
                lineHeight: 1.12,
                textWrap: 'balance',
                mb: 1.3,
              }}
            >
              A better live payment answer, with the surrounding economics and risk kept in line
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {caseStudy.howWeHelped.map((item, cardIndex) => {
              const columns =
                caseStudy.howWeHelped.length === 2 ? 6 : caseStudy.howWeHelped.length >= 4 ? 6 : 4;
              return (
                <Grid item xs={12} md={columns} key={item}>
                  <Box
                    sx={{
                      height: '100%',
                      position: 'relative',
                      border: '1px solid rgba(196,212,232,0.96)',
                      borderRadius: 1.25,
                      background:
                        cardIndex === 0
                          ? 'linear-gradient(180deg, #ffffff 0%, #f4f9ff 100%)'
                          : '#ffffff',
                      boxShadow: '0 14px 30px rgba(15,23,42,0.045)',
                      px: { xs: 2.4, md: 2.8 },
                      py: { xs: 2.4, md: 2.7 },
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background:
                          cardIndex === 0
                            ? 'linear-gradient(90deg, #0f6fff, #0ea5e9)'
                            : 'rgba(15,111,255,0.22)',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.66rem',
                        fontWeight: 400,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: cardIndex === 0 ? marketingPalette.accent : marketingPalette.muted,
                        mb: 1.2,
                        fontFamily: 'monospace',
                      }}
                    >
                      {`0${cardIndex + 1} · Move`}
                    </Typography>
                    <Typography
                      sx={{
                        color: marketingPalette.body,
                        lineHeight: 1.78,
                        fontSize: { xs: '0.98rem', md: '1rem' },
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ============ CURRENT -> OPTIMIZED -> RESULT (progression cards) ============ */}
      <Box component="section" sx={marketingSectionSx}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 740, mb: { xs: 3.5, md: 4.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.4 }}>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  fontWeight: 400,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: marketingPalette.accent,
                }}
              >
                Before, after, result
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  maxWidth: 72,
                  height: 1,
                  background: 'linear-gradient(90deg, rgba(15,111,255,0.4), rgba(15,111,255,0))',
                }}
              />
            </Box>
            <Typography
              component="h2"
              sx={{
                fontFamily: marketingDisplayFont,
                fontSize: { xs: '1.75rem', md: '2.35rem' },
                fontWeight: 400,
                letterSpacing: '-0.04em',
                color: marketingPalette.headline,
                lineHeight: 1.12,
                textWrap: 'balance',
                mb: 1.3,
              }}
            >
              The shape of the shift
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {caseStudy.optimizedState.map((item, idx) => {
              const isResult = idx === caseStudy.optimizedState.length - 1;
              const labelText = isResult
                ? 'Commercial result'
                : idx === 0
                  ? 'Current state'
                  : 'Optimized state';
              return (
                <Grid item xs={12} md={4} key={item.title}>
                  <Box
                    sx={{
                      height: '100%',
                      position: 'relative',
                      border: '1px solid rgba(196,212,232,0.96)',
                      borderRadius: 1.25,
                      background: isResult
                        ? 'linear-gradient(180deg, #ffffff 0%, #f4f9ff 100%)'
                        : '#ffffff',
                      boxShadow: '0 14px 30px rgba(15,23,42,0.045)',
                      px: { xs: 2.4, md: 2.8 },
                      py: { xs: 2.4, md: 2.7 },
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: isResult
                          ? 'linear-gradient(90deg, #0f6fff, #0ea5e9)'
                          : 'rgba(15,111,255,0.22)',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.66rem',
                        fontWeight: 400,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: isResult ? marketingPalette.accent : marketingPalette.muted,
                        mb: 1.2,
                        fontFamily: 'monospace',
                      }}
                    >
                      {`0${idx + 1} · ${labelText}`}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 400,
                        color: marketingPalette.headline,
                        fontSize: { xs: '1.02rem', md: '1.1rem' },
                        lineHeight: 1.3,
                        mb: 1.1,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: marketingPalette.body,
                        lineHeight: 1.78,
                        fontSize: '0.96rem',
                      }}
                    >
                      {item.body}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* =================== PROTECTION CASES (numbered bullets) ==================== */}
      {caseStudy.protectionCases.length ? (
        <Box component="section" sx={marketingMutedSectionSx}>
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 740, mb: { xs: 3.5, md: 4.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.4 }}>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.72rem',
                    fontWeight: 400,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: marketingPalette.accent,
                  }}
                >
                  Payment-program protection
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    maxWidth: 72,
                    height: 1,
                    background: 'linear-gradient(90deg, rgba(15,111,255,0.4), rgba(15,111,255,0))',
                  }}
                />
              </Box>
              <Typography
                component="h2"
                sx={{
                  fontFamily: marketingDisplayFont,
                  fontSize: { xs: '1.75rem', md: '2.35rem' },
                  fontWeight: 400,
                  letterSpacing: '-0.04em',
                  color: marketingPalette.headline,
                  lineHeight: 1.12,
                  textWrap: 'balance',
                  mb: 1.3,
                }}
              >
                Business priorities stayed visible — without distorting everyday decisions
              </Typography>
            </Box>

            <Box sx={{ maxWidth: 820, display: 'grid', gap: 1.5 }}>
              {caseStudy.protectionCases.map((item, bulletIndex) => (
                <Box
                  key={item}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '36px minmax(0, 1fr)',
                    gap: 1.6,
                    alignItems: 'start',
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: marketingPalette.accentSoft,
                      border: `1px solid ${marketingPalette.accentBorder}`,
                      color: marketingPalette.accent,
                      fontSize: '0.78rem',
                      fontWeight: 400,
                      fontFamily: 'monospace',
                      mt: 0.15,
                    }}
                  >
                    {bulletIndex + 1}
                  </Box>
                  <Typography
                    sx={{
                      color: marketingPalette.body,
                      lineHeight: 1.78,
                      fontSize: '0.98rem',
                      pt: 0.15,
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      ) : null}

      <MarketingFinalCta
        kicker="Case study · Next step"
        heading="Think a result like this is sitting in your stack? Let's find it."
        body="Send us the metric you'd most want to move. We'll say what's usually blocking it, where Ormal would start, and whether a health check or a demo is the faster path for you."
        secondary={{ label: 'Join interest list', href: INTEREST_LIST_MAILTO }}
      />

      <Footer />
    </Box>
  );
};

export default CaseStudyPage;
